/**
 * Het telefoonnummer aan de voordeur (W2): de landcode staat los van het
 * nummer, zoals in WhatsApp. Van landcode en getypt nummer maakt de server
 * één E.164-nummer (utils/telefoonnummer.server.ts, met libphonenumber,
 * alleen op de server). De landen zijn de lijst van de OTP-poort
 * (otp.server.ts).
 *
 * Geen .server: de deur gebruikt dit ook in de browser.
 */

export const LANDCODES = ['+31', '+32', '+49'] as const
export type Landcode = (typeof LANDCODES)[number]

export function isLandcode(waarde: unknown): waarde is Landcode {
	return (LANDCODES as readonly unknown[]).includes(waarde)
}

/** "+31612345678" wordt "+31 6 12345678": leesbaar in de zin, nooit om te rekenen. */
export function toonNummer(e164: string) {
	const land = LANDCODES.find((c) => e164.startsWith(c))
	if (!land) return e164
	const rest = e164.slice(land.length)
	if (land === '+31' && rest.startsWith('6'))
		return `${land} 6 ${rest.slice(1)}`
	return `${land} ${rest}`
}

/** De foutcodes van de OTP-laag, vertaald naar een zin uit de catalogus. */
export type TelFout =
	| 'auth.fout_tel_nummer'
	| 'auth.fout_tel_kort'
	| 'auth.fout_tel_lang'
	| 'auth.fout_tel_mobiel'
	| 'auth.fout_tel_dubbel'
	| 'auth.fout_tel_land'
	| 'auth.fout_tel_code'
	| 'auth.fout_tel_verlopen'
	| 'auth.fout_tel_druk'
	| 'auth.fout_tel_op_slot'
	| 'auth.fout_tel_dicht'
	| 'auth.fout_tel_weg'
	| 'auth.fout_tel_bezet'
	| 'auth.fout_tel_conflict'
	| 'auth.fout_tel_bewijs'
	| 'auth.fout_tel_netwerk'

/** `reden` hoort bij invalid_phone (te_kort, te_lang, geen_mobiel,
 *  dubbele_landcode): de zin zegt dan wat er mis is. Onbekend of zonder:
 *  de algemene zin. */
export function telFout(code: string | undefined, reden?: string): TelFout {
	switch (code) {
		case 'invalid_phone':
			return reden === 'te_kort'
				? 'auth.fout_tel_kort'
				: reden === 'te_lang'
					? 'auth.fout_tel_lang'
					: reden === 'geen_mobiel'
						? 'auth.fout_tel_mobiel'
						: reden === 'dubbele_landcode'
							? 'auth.fout_tel_dubbel'
							: 'auth.fout_tel_nummer'
		case 'country_not_supported':
			return 'auth.fout_tel_land'
		case 'otp_wrong':
			return 'auth.fout_tel_code'
		case 'otp_expired':
			return 'auth.fout_tel_verlopen'
		case 'rate_limited':
			return 'auth.fout_tel_druk'
		case 'otp_locked':
			return 'auth.fout_tel_op_slot'
		case 'number_locked':
			return 'auth.fout_tel_dicht'
		case 'otp_unavailable':
			return 'auth.fout_tel_weg'
		case 'phone_taken':
			return 'auth.fout_tel_bezet'
		case 'phone_conflict':
			return 'auth.fout_tel_conflict'
		case 'ticket_invalid':
			return 'auth.fout_tel_bewijs'
		default:
			return 'auth.fout_tel_netwerk'
	}
}
